import { errorMessage } from "@esp/api-client";
import { Alert, Button, Field, Input } from "@esp/ui";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useState, type FormEvent } from "react";

import { auth, session, unwrap } from "../app/api";
import { STAFF_QUERY_KEY } from "./staff";

/** Staff sign-in: password, then the authenticator code (always required for staff). */
export function LoginPage({ notice }: { notice?: string | undefined }) {
  const queryClient = useQueryClient();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [code, setCode] = useState("");
  const [needsCode, setNeedsCode] = useState(false);
  const done = () => queryClient.invalidateQueries({ queryKey: STAFF_QUERY_KEY });

  const login = useMutation({
    mutationFn: () => unwrap(
      // Marks an admin-console sign-in: the server then asks for the second factor (ADR-0037).
      auth.POST("/api/v1/auth/login", { body: { email, password }, headers: { "X-ESP-Console": "admin" } }),
    ),
    onSuccess: async (result) => {
      session.setCsrfToken(result.csrf_token);
      if (result.mfa_required) setNeedsCode(true);
      else await done();
    },
  });
  const verify = useMutation({
    mutationFn: () => unwrap(auth.POST("/api/v1/auth/mfa/verify", { body: { code: code.trim() } })),
    onSuccess: async (result) => {
      session.setCsrfToken(result.csrf_token);
      await done();
    },
  });
  const error = login.error ?? verify.error;

  function submit(event: FormEvent) {
    event.preventDefault();
    if (needsCode) verify.mutate();
    else login.mutate();
  }

  return (
    <div className="flex min-h-screen flex-col items-center justify-center gap-8 bg-slate-900 px-4">
      <img src="/brand/logo.png" alt="" className="h-14 w-auto" />
      <main className="w-full max-w-sm rounded-lg bg-white px-6 py-8 shadow-xl">
        <p className="text-xs font-semibold tracking-widest text-red-700 uppercase">Staff only</p>
        <h1 className="mt-1 text-xl font-semibold text-slate-900">Platform admin</h1>
        <form noValidate className="mt-6 space-y-4" onSubmit={submit}>
          {notice && <Alert tone="info">{notice}</Alert>}
          {error && <Alert tone="error">{errorMessage(error)}</Alert>}
          {!needsCode ? (
            <>
              <Field label="Email">
                {({ id }) => <Input id={id} type="email" autoComplete="username" autoFocus value={email} onChange={(e) => setEmail(e.target.value)} />}
              </Field>
              <Field label="Password">
                {({ id }) => <Input id={id} type="password" autoComplete="current-password" value={password} onChange={(e) => setPassword(e.target.value)} />}
              </Field>
            </>
          ) : (
            <Field label="Authenticator code">
              {({ id }) => <Input id={id} autoComplete="one-time-code" autoFocus value={code} onChange={(e) => setCode(e.target.value)} />}
            </Field>
          )}
          <Button type="submit" className="w-full" loading={login.isPending || verify.isPending}>
            {needsCode ? "Verify" : "Sign in"}
          </Button>
          <p className="text-xs text-slate-500">
            Staff accounts need two-factor authentication. Every action here is recorded.
          </p>
        </form>
      </main>
    </div>
  );
}
